import { useSyncExternalStore } from 'react'
import { notifyAbilityListeners } from '@/lib/rbac'

const DEBUG_ABILITIES_KEY = 'vixiees:debug-abilities'

type AbilityDefinition = { identifier: string; group: string }

export const HEART_ABILITIES: AbilityDefinition[] = [
  { identifier: 'super_admin', group: 'Admin' },
  { identifier: 'manage_vixiees_users', group: 'Admin' },
]

let debugAbilities: string[] | null = import.meta.env.DEV
  ? (() => {
      const stored = sessionStorage.getItem(DEBUG_ABILITIES_KEY)

      if (!stored) return null

      try {
        return JSON.parse(stored) as string[]
      } catch {
        return null
      }
    })()
  : null

const debugListeners = new Set<() => void>()

function notifyDebugListeners() {
  debugListeners.forEach((listener) => listener())
  notifyAbilityListeners() // also notify RBAC subscribers so useHasAbility re-renders
}

function subscribeDebug(onStoreChange: () => void) {
  debugListeners.add(onStoreChange)

  return () => debugListeners.delete(onStoreChange)
}

export { subscribeDebug as subscribeDebugAbilities }

function getDebugSnapshot(): string[] | null {
  return debugAbilities
}

function setDebug(value: string[] | null) {
  if (value === null) {
    sessionStorage.removeItem(DEBUG_ABILITIES_KEY)
  } else {
    sessionStorage.setItem(DEBUG_ABILITIES_KEY, JSON.stringify(value))
  }

  debugAbilities = value
  notifyDebugListeners()
}

/**
 * Pure getter for non-component code (route guards).
 */
export function getDebugAbilities(): string[] | null {
  if (!import.meta.env.DEV) return null

  return debugAbilities
}

/**
 * React hook for the AbilitiesDebugger UI component.
 */
export function useDebugAbilities() {
  const currentDebug = useSyncExternalStore(subscribeDebug, getDebugSnapshot)
  const isActive = currentDebug !== null
  const currentAbilities = currentDebug ?? []

  const toggleAbility = (identifier: string, realAbilities: string[]) => {
    const base = isActive ? currentAbilities : realAbilities
    const updated = base.includes(identifier)
      ? base.filter((ability) => ability !== identifier)
      : [...base, identifier]
    setDebug(updated)
  }

  const deactivate = () => setDebug(null)

  return {
    abilities: currentAbilities,
    isActive,
    toggleAbility,
    deactivate,
    allAbilities: HEART_ABILITIES,
  }
}
